import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {SignInEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private';
import {PublicFlowEnum} from '../constants/PublicFlowEnum';
import {LOCAL_STORAGE_KEYS} from '../forgot-password/constants/localStorageKeys';
import {sendForgotPasswordToken} from '../services/sendForgotPasswordToken';
import {useCancelSignUpFlowStore} from '../sign-up/store/useCancelSignUpFlowStore';
import {usePublicFlowStore} from '../store/usePublicFlowStepStore';
import {useForgotPasswordData} from './useForgotPasswordData';

let triggerSuccessSnackbar: (() => void) | null = null;

export const setSnackbarTrigger = (trigger: () => void) => {
	triggerSuccessSnackbar = trigger;
};

export const getSnackbarTrigger = () => triggerSuccessSnackbar;

export function useSteps(type: PublicFlowEnum) {
	const navigate = useNavigate();
	const modalState = useCancelSignUpFlowStore(state => state.modalState);
	const setModalState = useCancelSignUpFlowStore(state => state.setModalState);
	const resetModalState = useCancelSignUpFlowStore(state => state.resetModalState);
	const currentStepState = usePublicFlowStore(state => state.steps[type]);
	const setCurrentStepState = usePublicFlowStore(state => state.setStep);
	const resetSteps = usePublicFlowStore(state => state.resetStep);
	const {data: forgotPasswordData, setForgotPasswordData} = useForgotPasswordData();
	const {
		dataHostNameState: {productCode},
	} = useHostName();
	const {handleLogout} = useAccountInformation();

	const isFirstStep = currentStepState === 1;

	const handleClose = useCallback(() => {
		if (type === PublicFlowEnum.SIGN_UP) {
			setModalState(true);
			return;
		}
		resetSteps(type);
		navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
	}, [type]);

	const handleConfirmationModalClose = useCallback((privateFlow?: boolean) => {
		resetSteps(type);
		if (privateFlow) {
			handleLogout();

			return;
		}
		navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
	}, []);

	const handleNextStep = useCallback(() => {
		setCurrentStepState(type, prevCurrentStepState => prevCurrentStepState + 1);
	}, []);

	const handleHelpStep = useCallback(() => {
		setCurrentStepState(type, 4);
	}, []);

	const handleCodeStep = useCallback(() => {
		const cameFromNoContactModal =
			localStorage.getItem(LOCAL_STORAGE_KEYS.CAME_FROM_NO_CONTACT_MODAL) === 'true';

		if (cameFromNoContactModal && type === PublicFlowEnum.FORGOT_PASSWORD) {
			localStorage.removeItem(LOCAL_STORAGE_KEYS.CAME_FROM_NO_CONTACT_MODAL);
			setCurrentStepState(type, 1);
			return;
		}

		setCurrentStepState(type, 2);
	}, [type]);

	const handleBackStep = useCallback(() => {
		resetModalState();
		if (isFirstStep) {
			navigate(SignInEnum.SIGN_IN, {state: {goToSignInState: true}});
			return;
		}

		if (type === PublicFlowEnum.FORGOT_PASSWORD && currentStepState === 3) {
			setCurrentStepState(type, 2);
			sendForgotPasswordToken({
				cdEmpresaPlano: forgotPasswordData.cdEmpresaPlano,
				dtNascimento: forgotPasswordData.dtNascimento,
				id: forgotPasswordData.id,
				tipoId: forgotPasswordData.tipoId,
			})
				.then(response => {
					if (response?.content) {
						setForgotPasswordData(state => ({
							...state,
							cdEmpresaPlano: forgotPasswordData.cdEmpresaPlano,
							dtNascimento: forgotPasswordData.dtNascimento,
							id: forgotPasswordData.id,
							tipoId: forgotPasswordData.tipoId,
							emailMascarado: response.content.emailMascarado,
							telefoneMascarado: response.content.telefoneMascarado,
						}));
						if (triggerSuccessSnackbar) {
							triggerSuccessSnackbar();
						}
					}
				})
				.catch(() => {});
			return;
		}

		setCurrentStepState(type, prevCurrentStepState => prevCurrentStepState - 1);
	}, [isFirstStep, type, currentStepState, productCode, forgotPasswordData]);

	return {
		isFirstStep,
		handleClose,
		handleNextStep,
		handleHelpStep,
		handleCodeStep,
		handleBackStep,
		currentStepState,
		modalState,
		setModalState,
		resetSteps,
		resetModalState,
		handleConfirmationModalClose,
	};
}
